import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Alert,
  LinearProgress
} from '@mui/material';
import MainLayout from '../../layout/MainLayout';
import SearchIcon from '@mui/icons-material/Search';
import FolderIcon from '@mui/icons-material/Folder';
import StarIcon from '@mui/icons-material/Star';
import GroupIcon from '@mui/icons-material/Group';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useNavigate } from 'react-router-dom';
import WorkflowGuide from '../../components/olymp/WorkflowGuide';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/authContext';
import { useAppearance } from '../../context/AppearanceContext';
import DashboardOnboardingDialog from '../../components/olymp/DashboardOnboardingDialog';

// Import JSON data
import {
  getCatalogStats,
  getWorkingLists,
  getDataRequests,
  getCohorts,
  resolveFavoritesList,
} from '../../services/catalogService';

/* Die Seite las bis 2026-08-30 fuenf Dateien aus `src/data/` -- und rechnete die
   Kacheln daraus, unter dem Kommentar "Calculate stats from real data". Auf
   hospital1 stand deshalb **9** an der Parameterkachel, waehrend der Katalog
   3900 Karten fuehrt: `parameters.json` hat neun Eintraege mit den IDs 1-9.
   Die echten Endpunkte gab es zu dem Zeitpunkt laengst und sie antworteten
   alle mit 200 (#227). */

/* Fuer "Letzte Aktivitaeten" und "Benachrichtigungen" gibt es **keine** Quelle:
   das Modell `CatalogUserActivityLog` existiert im Backend, ein ViewSet dafuer
   ist nicht registriert; Benachrichtigungsobjekte gibt es gar nicht, nur ein
   Einstellungsfeld am Nutzer. Beide Bereiche bleiben deshalb ausgeblendet,
   statt erfundene Eintraege zu zeigen -- dieselbe Regel wie bei der
   Werteverteilung (#215): eine leere Karte ist die bessere Auskunft als eine
   plausible falsche. Sobald es Endpunkte gibt, wird aus der Konstante ein
   Ladevorgang. */
const ZEIGE_UNBELEGTE_BEREICHE = false;

// Helper function to format time ago
const getTimeAgo = (timestamp, t) => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffMs = now - activityTime;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffHours < 1) return t('dashboard.timeAgo.fewMinutes');
  if (diffHours < 24) return t('dashboard.timeAgo.hours', { count: diffHours });
  if (diffDays === 1) return t('dashboard.timeAgo.oneDay');
  return t('dashboard.timeAgo.days', { count: diffDays });
};

const getOnboardingStorageKey = (currentUser) => {
  const userKey = currentUser?.id || currentUser?.email || 'unknown-user';
  return `olymp-dashboard-onboarding-seen-v1:${userKey}`;
};

const OlympDashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { appearance } = useAppearance();
  const widgets = appearance.dashboardWidgets ?? {};
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('pt')
    ? 'pt-BR'
    : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : 'en-US';
  const currentUserName = user?.name || user?.email || '';
  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  /* `Number(value) || 0` hat aus `null` eine 0 gemacht -- "nicht erreichbar"
     war von "es gibt keine" nicht zu unterscheiden. Jetzt: 0 bleibt 0,
     unbekannt wird ein Gedankenstrich. */
  const formatNumber = (value) =>
    value === null || value === undefined || Number.isNaN(Number(value))
      ? '\u2013'
      : numberFormatter.format(Number(value));

  const [favorites, setFavorites] = useState(null);
  const [myLists, setMyLists] = useState([]);
  const [alleListen, setAlleListen] = useState([]);
  const [katalogStats, setKatalogStats] = useState(null);
  const [anzahlAnfragen, setAnzahlAnfragen] = useState(null);
  const [anzahlKohorten, setAnzahlKohorten] = useState(null);
  const [laedt, setLaedt] = useState(true);
  const [ladefehler, setLadefehler] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  useEffect(() => {
    let abgemeldet = false;

    const laden = async () => {
      setLaedt(true);
      const [statsErg, listenErg, anfragenErg, kohortenErg] = await Promise.all([
        getCatalogStats(),
        getWorkingLists(),
        getDataRequests(),
        getCohorts(),
      ]);
      if (abgemeldet) return;

      /* Jeder Dienstaufruf meldet seinen Ausgang selbst. Schlaegt einer fehl,
         bleibt sein Wert `null` und die Kachel zeigt einen Strich -- **keine
         Null**. Eine 0 ist eine Aussage ("es gibt keine Kohorten") und darf
         nicht dasselbe bedeuten wie "nicht erreichbar". */
      setKatalogStats(statsErg.source === 'api' ? statsErg.stats : null);
      setAnzahlAnfragen(anfragenErg.source === 'api' ? anfragenErg.requests.length : null);
      setAnzahlKohorten(kohortenErg.source === 'api' ? kohortenErg.cohorts.length : null);

      const listen = listenErg.source === 'api' ? listenErg.lists : [];
      setAlleListen(listen);

      const eigeneListen = listen.filter(
        (list) => (currentUserName && list.createdBy === currentUserName) || list.isPublic
      );
      setMyLists(eigeneListen.slice(0, 3));

      /* Favoriten sind im Backend eine Arbeitsliste, keine eigene Sache.
         Vorher stand hier `localStorage.getItem('olymp_favorites')` -- ein
         Schluessel, der **nirgends im Projekt geschrieben wird**. Die Kachel
         zeigte darum immer 0, unabhaengig davon, was der Nutzer markiert hat.

         Die Anzahl stimmt jetzt. Die fuenf Eintraege darunter brauchen die
         Namen zu `parameterIds`, und dafuer fehlt dem Katalog-Endpunkt ein
         `ids`-Filter -- ihn ohne einen zu bauen hiesse 3900 Karten laden, um
         fuenf zu zeigen (#193 hat die Liste gerade von 40 Anfragen auf 1
         gesenkt). Nachgezogen in #233.

         Die Aufloesung kommt seit #255 aus `resolveFavoritesList` -- derselben
         Funktion, die auch der Stern im Katalog benutzt. Zwei eigene Kopien
         dieser Regel hatten schon einmal auseinanderlaufen koennen, ohne dass
         ein Test es gemerkt haette.

         Rueckmeldung Runde 2: `listen` wird bei einem nicht erreichbaren
         Dienst zu `[]`, damit die Listen-Kacheln unten nicht abstuerzen --
         genau das liess die Favoritenkachel vorher "0" und das Widget "Noch
         keine Favoriten" zeigen, wenn /api/olymp/lists/ ausfiel. `favorites`
         bleibt deshalb nur bei einer echten API-Antwort ein Array; sonst
         `null` ("nicht bekannt"), damit die Kachel den Strich bekommt und
         das Widget nicht behauptet, es gaebe wirklich keine Favoriten. */
      if (listenErg.source === 'api') {
        const favoritenListe = resolveFavoritesList(listen);
        setFavorites(favoritenListe ? favoritenListe.parameterIds : []);
      } else {
        setFavorites(null);
      }

      setLadefehler(
        statsErg.source !== 'api' ||
          listenErg.source !== 'api' ||
          anfragenErg.source !== 'api' ||
          kohortenErg.source !== 'api'
      );
      setLaedt(false);
    };

    laden();
    return () => {
      abgemeldet = true;
    };
  }, [currentUserName]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setIsOnboardingOpen(false);
      return;
    }

    const storageKey = getOnboardingStorageKey(user);
    const hasSeenOnboarding = localStorage.getItem(storageKey) === '1';
    setIsOnboardingOpen(!hasSeenOnboarding);
  }, [authLoading, user]);

  const dismissOnboarding = () => {
    if (user) {
      localStorage.setItem(getOnboardingStorageKey(user), '1');
    }
    setIsOnboardingOpen(false);
  };

  /* `null` heisst "nicht bekannt" und wird als Strich gezeigt, 0 als 0.
     `myRequests` zaehlte vorher die **Kohorten** -- laut Kommentar
     absichtlich, "Anzahl der Anfragen basierend auf Kohorten". Das sind zwei
     verschiedene Dinge und auf hospital1 zufaellig beide null. */
  const stats = {
    totalParameters: katalogStats ? katalogStats.totalParameters : null,
    omopMapped: katalogStats ? katalogStats.mappedCount : null,
    pendingMappings: katalogStats ? katalogStats.pendingCount : null,
    favoritesCount: favorites === null ? null : favorites.length,
    myLists: alleListen.length,
    myCohorts: anzahlKohorten,
    myRequests: anzahlAnfragen,
  };

  /* Kein Endpunkt -- siehe ZEIGE_UNBELEGTE_BEREICHE oben. */
  const recentActivity = [];
  const notifications = [];

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1 }}>
        <DashboardOnboardingDialog
          open={isOnboardingOpen}
          onDismiss={dismissOnboarding}
        />

        {/* Welcome Section */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            mb: 3
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {t('dashboard.hero.title')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {t('dashboard.hero.subtitle')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SearchIcon />}
              onClick={() => navigate('/catalog')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5
              }}
            >
              {t('dashboard.hero.catalogCta')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => navigate('/cohorts')}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              {t('dashboard.hero.cohortsCta')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AssignmentIcon />}
              onClick={() => navigate('/requests')}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              {t('dashboard.hero.requestsCta')}
            </Button>
          </Box>
        </Paper>

        {/* `laedt`/`ladefehler` wurden bisher nur gesetzt und nie gelesen (Rueckmeldung
            Runde 1) -- die einzelnen Kacheln zeigten einen Strich fuer einen nicht
            erreichbaren Dienst, aber nichts sagte sichtbar, dass ueberhaupt ein
            Fehler vorlag oder noch geladen wird. */}
        {laedt && <LinearProgress sx={{ mb: 2 }} />}
        {!laedt && ladefehler && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('dashboard.loadError')}
          </Alert>
        )}

        <WorkflowGuide />

        {/* Stats Cards */}
        {widgets.statistics !== false && <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.parametersInCatalog')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.totalParameters)}
                    </Typography>
                  </Box>
                  <FolderIcon sx={{ fontSize: 50, color: 'primary.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.omopMapped')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.omopMapped)}
                    </Typography>
                    <Typography variant="caption" color="success.main" fontWeight="600">
                      {stats.omopMapped !== null && stats.totalParameters
                        ? t('dashboard.stats.coverage', {
                            value: Math.round((stats.omopMapped / stats.totalParameters) * 100),
                          })
                        : ''}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 50, color: 'success.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/catalog')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.myFavorites')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.favoritesCount)}
                    </Typography>
                  </Box>
                  <StarIcon sx={{ fontSize: 50, color: 'warning.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                }
              }}
              onClick={() => navigate('/requests')}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      {t('dashboard.stats.dataRequests')}
                    </Typography>
                    <Typography variant="h4" sx={{ mt: 1 }}>
                      {formatNumber(stats.myRequests)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.stats.cohortsCount', { count: formatNumber(stats.myCohorts) })}
                    </Typography>
                  </Box>
                  <AssignmentIcon sx={{ fontSize: 50, color: 'info.main', opacity: 0.3 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>}

        {/* Content Grid */}
        <Grid container spacing={3}>
          {/* Favorites Widget */}
          {widgets.incomingRequests !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    <StarIcon sx={{ verticalAlign: 'middle', mr: 1, color: 'warning.main' }} />
                    {t('dashboard.widgets.favorites')}
                  </Typography>
                  {favorites && favorites.length > 0 && (
                    <Button
                      size="small"
                      onClick={() => navigate('/catalog')}
                    >
                      {t('dashboard.widgets.viewAll')}
                    </Button>
                  )}
                </Box>
                {/* Favoriten sind im Backend eine Arbeitsliste und liefern nur
                    `parameterIds`. Die Namen dazu brauchen einen `ids`-Filter am
                    Katalog-Endpunkt, den es noch nicht gibt (#233) -- bis dahin
                    steht hier die belastbare Anzahl mit dem Weg zur Liste, statt
                    fuenf Eintraege zu erfinden oder 3900 Karten zu laden, um
                    fuenf zu zeigen. */}
                {favorites === null ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <StarIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('dashboard.widgets.favoritesUnavailable')}
                    </Typography>
                  </Box>
                ) : favorites.length > 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <StarIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.5, mb: 2 }} />
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {formatNumber(favorites.length)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('dashboard.widgets.favoritesCount', { count: favorites.length })}
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => navigate('/catalog')}>
                      {t('dashboard.widgets.viewAll')}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <StarIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('dashboard.widgets.noFavorites')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {t('dashboard.widgets.noFavoritesHint')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/catalog')}
                    >
                      {t('dashboard.widgets.goToCatalog')}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>}

          {/* Recent Activity -- ohne Endpunkt ausgeblendet, siehe
              ZEIGE_UNBELEGTE_BEREICHE. `CatalogUserActivityLog` gibt es als
              Modell, ein ViewSet dafuer ist nicht registriert. */}
          {ZEIGE_UNBELEGTE_BEREICHE && widgets.activityLog !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {t('dashboard.widgets.recentActivity')}
                </Typography>
                <List dense>
                  {recentActivity.map(activity => (
                    <ListItem key={activity.id}>
                      <ListItemText
                        primary={activity.text}
                        secondary={activity.time}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>}

          {/* My Lists */}
          {widgets.runningAnalyses !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight="bold">
                    {t('dashboard.widgets.myLists')}
                  </Typography>
                  <IconButton size="small" color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
                <List dense>
                  {myLists.map(list => (
                    <ListItem
                      key={list.id}
                      button
                      onClick={() => navigate('/catalog')}
                      secondaryAction={
                        <Typography variant="body2" color="textSecondary">
                          {t('dashboard.widgets.parameterCount', { count: formatNumber(list.parameterIds.length) })}
                        </Typography>
                      }
                    >
                      <ListItemIcon>
                        <FolderIcon color={list.isPublic ? 'primary' : 'action'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={list.name}
                        secondary={list.isPublic ? t('dashboard.widgets.public') : t('dashboard.widgets.private')}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>}

          {/* Notifications -- ohne Endpunkt ausgeblendet, siehe
              ZEIGE_UNBELEGTE_BEREICHE. Es gibt keine Benachrichtigungsobjekte,
              nur `notification_settings` am Nutzer. */}
          {ZEIGE_UNBELEGTE_BEREICHE && widgets.paymentOverview !== false && <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  <NotificationsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {t('dashboard.widgets.notifications')}
                  {notifications.filter(n => n.unread).length > 0 && (
                    <Chip
                      label={formatNumber(notifications.filter(n => n.unread).length)}
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <List dense>
                  {notifications.map(notif => (
                    <ListItem
                      key={notif.id}
                      sx={{
                        backgroundColor: notif.unread ? 'action.hover' : 'transparent',
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemText
                        primary={notif.text}
                        primaryTypographyProps={{
                          fontWeight: notif.unread ? 600 : 400
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>}
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default OlympDashboard;
